<template>
  <div>
    <b-card>
      <breadcrumbs
        :breadcrumb-items="breadcrumbItems"
      />
      <PartnerToolbar
        v-if="Object.keys(partner).length !== 0"
        :partner="partner"
      />
    </b-card>
    <StatsNavigation />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-top">
            <h4>{{ $t('Combined Statistics') }}</h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              v-model="rangePicker"
              :is-loading="loading"
              @range-change="toggleRange"
              @refresh="refreshData"
            />
          </div>
        </div>
      </div>
      <div
        v-if="loading"
        class="d-flex justify-content-center mb-1"
      >
        <b-spinner label="Loading..." />
      </div>
      <div v-else>
        <b-table
          small
          :items="statsV2Data"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          responsive
          striped
          foot-clone
        >
          <template #head(date)>
            {{ $t('message.tableHeader.date') }}
          </template>

          <template #head(impressions)>
            {{ $t('message.tableHeader.impressions') }}
          </template>

          <template #head(clicks)>
            {{ $t('message.tableHeader.clicks') }}
          </template>

          <template #head(revenue)>
            {{ $t('message.tableHeader.ecpm') }}
          </template>

          <template #cell(date)="row">
            <span v-if="row">
              <strong>{{ getFormattedDate(new Date(row.item.date)) }}</strong>
            </span>
            <span v-else>
              {{ row.item.date }}
            </span>
          </template>

          <template #cell(impressions)="data">
            {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
          </template>

          <template #cell(clicks)="data">
            {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(countObjectTotals(data.value)) : 0 }}
          </template>

          <template #cell(revenue)="data">
            {{ currency('USD', countObjectTotals(data.value)) }}
          </template>

          <template #foot(date)="data">
            <strong :data-label="data.label">{{ $t('Records') }}: {{ statsV2Data.length }}</strong>
          </template>

          <template #foot(impressions)="data">
            <strong :data-label="data.label">{{ totalFormat('impressions').toLocaleString('Ru-ru') }}</strong>
          </template>

          <template #foot(clicks)="data">
            <strong :data-label="data.label">{{ totalFormat('clicks').toLocaleString('Ru-ru') }}</strong>
          </template>

          <template #foot(revenue)="data">
            <strong :data-label="data.label">{{ currency('USD', totalFormat('revenue')) }}</strong>
          </template>
        </b-table>
      </div>
    </b-card>
  </div>
</template>

<script>
import { getUserData } from '@/auth/utils'
import useJwt from '@/auth/jwt/useJwt'
import Ripple from 'vue-ripple-directive'
import {
  BCard,
  BTable,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import currencyFormatter from '@/utils/currency-formatter'
import getFormattedDate from '@/utils/date-formatter'
import PartnerToolbar from './PartnerToolbar.vue'
import StatsNavigation from './stats/StatsNavigation.vue'
import StatsDateRangeFilter from './stats/StatsDateRangeFilter.vue'

export default {
  components: {
    Breadcrumbs,
    BCard,
    BTable,
    BSpinner,
    PartnerToolbar,
    StatsNavigation,
    StatsDateRangeFilter,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      loading: true,
      pageTitle: '',
      pageSubtitle: '',
      breadcrumbItems: [],
      partner: {},
      fields: [
        { key: 'date', label: 'Date', tdClass: 'td-width' },
        { key: 'impressions', label: 'Impressions', tdClass: 'td-width' },
        { key: 'clicks', label: 'Clicks', tdClass: 'td-width' },
        { key: 'revenue', label: 'Revenue', tdClass: 'td-width' },
      ],
      sortBy: 'date',
      sortDesc: true,
      sortDirection: 'desc',
      authUserData: getUserData(),
      currency: currencyFormatter,
      getFormattedDate,
      statsV2Data: [],
      statsTotalData: [],
      rangePicker: [
        getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        getFormattedDate(new Date()),
      ],
      rangeDefault: [
        getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        getFormattedDate(new Date()),
      ],
    }
  },
  created() {
    this.getPartnerData()
  },
  methods: {
    async getPartnerData() {
      const responseData = await useJwt.getPartner(this.$route.params.id)
      this.partner = responseData.data.partner || {}
      this.setBreabcrumbs()
      this.getStatsV2Data()
      this.getStatsTotalData()
    },
    async getStatsV2Data() {
      this.loading = true
      this.statsV2Data = []

      if (!this.rangePicker || this.rangePicker.length < 2) {
        this.loading = false
        return
      }

      // Phoenix Statistics
      const phoenixParams = {
        campaign_id: this.$route.params.id,
        type: 'daily',
        from: this.rangePicker[0],
        to: this.rangePicker[1],
        ad_type: 'external',
      }

      // DSP Statistics
      const dspParams = {
        from: this.rangePicker[0],
        to: this.rangePicker[1],
        type: 'daily',
        openrtb_partner_id: this.$route.params.id,
      }

      try {
        // Make both requests in parallel
        const [phoenixResponse, dspResponse] = await Promise.all([
          useJwt.getCampaignStatsEcpm(phoenixParams),
          useJwt.getDsppartnerStatsRtb(null, dspParams),
        ])

        const phoenixData = phoenixResponse.data.items || []
        const dspData = dspResponse.data.items || []

        // Merge data by dates
        this.statsV2Data = this.mergeStatsData(phoenixData, dspData)
      } catch (error) {
        this.statsV2Data = []
      }

      this.loading = false
    },
    async getStatsTotalData() {
      this.statsTotalData = []

      if (!this.rangePicker || this.rangePicker.length < 2) {
        return
      }

      // Phoenix Total Statistics
      const phoenixParams = {
        campaign_id: this.$route.params.id,
        type: 'daily',
        from: this.rangePicker[0],
        to: this.rangePicker[1],
        ad_type: 'external',
      }

      // DSP Total Statistics
      const dspParams = {
        from: this.rangePicker[0],
        to: this.rangePicker[1],
        type: 'daily',
        openrtb_partner_id: this.$route.params.id,
      }

      try {
        // Make both requests in parallel
        const [phoenixResponse, dspResponse] = await Promise.all([
          useJwt.getPartnersCampaignsTotal(phoenixParams),
          useJwt.getDsppartnerStatsRtb(null, dspParams),
        ])

        const phoenixData = phoenixResponse.data.items || []
        const dspData = dspResponse.data.items || []

        // Merge data by dates
        this.statsTotalData = this.mergeStatsData(phoenixData, dspData)
      } catch (error) {
        this.statsTotalData = []
      }
    },
    setBreabcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$t('RTB partners'),
          to: '/partners/',
        },
        {
          text: this.partner.name,
          to: `/partners/${this.partner._id}/`,
        },
        {
          text: this.$t('Combined Statistics'),
          active: true,
        },
      ]
    },
    countObjectTotals(obj) {
      if (typeof obj !== 'object' || obj === null) {
        return Number(obj) || 0
      }
      const calc = Object.values(obj).reduce((acc, value) => acc + value, 0)
      return calc
    },
    mergeStatsData(phoenixData, dspData) {
      // Create Map for fast date lookup
      const phoenixMap = new Map()
      const dspMap = new Map()

      // Fill Maps
      phoenixData.forEach(item => {
        phoenixMap.set(item.date, item)
      })
      dspData.forEach(item => {
        dspMap.set(item.date, item)
      })

      // Get all unique dates
      const allDates = new Set([...phoenixMap.keys(), ...dspMap.keys()])
      const mergedData = []

      // Merge data for each date
      allDates.forEach(date => {
        const phoenixItem = phoenixMap.get(date) || {}
        const dspItem = dspMap.get(date) || {}

        const mergedItem = {
          date,
          impressions: (phoenixItem.impressions || 0) + (dspItem.impressions || 0),
          clicks: this.mergeObjectValues(phoenixItem.clicks, dspItem.clicks),
          revenue: this.mergeObjectValues(phoenixItem.revenue, dspItem.revenue),
        }

        mergedData.push(mergedItem)
      })

      // Sort by date descending (newest dates first)
      return mergedData.sort((a, b) => new Date(b.date) - new Date(a.date))
    },
    mergeObjectValues(obj1, obj2) {
      // If both values are objects, merge them
      if (typeof obj1 === 'object' && obj1 !== null && typeof obj2 === 'object' && obj2 !== null) {
        const merged = { ...obj1 }
        Object.keys(obj2).forEach(key => {
          merged[key] = (merged[key] || 0) + (obj2[key] || 0)
        })
        return merged
      }

      // If one is object and other is number - add number to object
      if (typeof obj1 === 'object' && obj1 !== null && typeof obj2 === 'number') {
        const merged = { ...obj1 }
        merged.dsp_addition = (merged.dsp_addition || 0) + obj2
        return merged
      }
      if (typeof obj2 === 'object' && obj2 !== null && typeof obj1 === 'number') {
        const merged = { ...obj2 }
        merged.phoenix_addition = (merged.phoenix_addition || 0) + obj1
        return merged
      }

      // If only one object, return it
      if (typeof obj1 === 'object' && obj1 !== null) {
        return obj1
      }
      if (typeof obj2 === 'object' && obj2 !== null) {
        return obj2
      }

      // If both numbers, return sum
      let val1 = 0
      if (typeof obj1 === 'number') {
        val1 = obj1
      } else if (typeof obj1 === 'object' && obj1 !== null) {
        val1 = this.countObjectTotals(obj1)
      }

      let val2 = 0
      if (typeof obj2 === 'number') {
        val2 = obj2
      } else if (typeof obj2 === 'object' && obj2 !== null) {
        val2 = this.countObjectTotals(obj2)
      }

      return parseFloat(val1) + parseFloat(val2)
    },
    totalFormat(field) {
      let total = 0
      this.statsV2Data.forEach(i => {
        if (typeof i[field] === 'object') {
          total += +this.countObjectTotals(i[field])
        } else {
          total += i[field]
        }
      })
      return total
    },
    toggleRange(e) {
      this.rangePicker = e
      this.getStatsV2Data()
      this.getStatsTotalData()
      this.$router.replace({
        name: 'dashboard-partner-stats',
        query: { from: e[0], to: e[1] },
      })
    },
    refreshData() {
      this.getStatsV2Data()
      this.getStatsTotalData()
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
<style>
  .col-picker {
    min-width: 220px;
  }
</style>
<style>
[dir] .table-responsive .table-responsive {
  margin-bottom: 0!important;
}

[dir] .table.b-table > tbody > tr.b-table-details > td {
  padding-left: 0!important;
  padding-right: 0!important;
  padding-bottom: 0!important;
  border-bottom: 1px solid #776bf0
}

[dir] .table.b-table > tbody > tr.b-table-details .table-striped tbody tr:nth-of-type(odd) {
  background: #ebebeb;
}

[dir] .table.b-table > tbody td.td-width {
  width: 11%;
}
</style>
<style scoped>
  [dir] .b-table-has-details .btn-flat-primary {
    padding: 0!important;
  }
</style>
